import {useRef, useState, useEffect, lazy, Suspense} from 'react'
import {Hero} from '@primer/react-brand'
import type Assets from './webgl-utils/assets'

//import IntroHero from './IntroHero'
import {Spacer} from './_shared'
import HeroCarousel from './components/HeroCarousel/HeroCarousel'
import HeroCarouselControls, {
  type HeroCarouselControlsProps,
} from './components/HeroCarousel/HeroCarouselControls/HeroCarouselControls'
import CtaForm from './components/CtaForm/CtaForm'
import Logos from './components/Logos/Logos'
import {COPY} from './Intro.data'
import {useDomReady} from '../../lib/utils/platform'
import {isFeatureEnabled} from '@github-ui/feature-flags'
const IntroHeroUniverse = lazy(() => import('./IntroHeroUniverse'))
const IntroHero = lazy(() => import('./IntroHero'))

export default function Intro({assetsRef}: {assetsRef: React.RefObject<Assets | null>}) {
  const isUniverseAnimationsEnabled = isFeatureEnabled('site_homepage_universe_animations')
  const [currentCarouselScreenIndex, setCurrentCarouselScreenIndex] = useState<number>(0)
  const heroRef = useRef<HTMLDivElement | null>(null)
  const [isAssetsLoaded, setIsAssetsLoaded] = useState<boolean>(false)
  const isDomReady = useDomReady()

  const setCopyScrollOpacity = (copyScrollOpacity: number) => {
    if (heroRef.current) {
      heroRef.current.style.opacity = `${copyScrollOpacity}` // Set the desired opacity value
    }
  }

  const setCopyScrollScale = (copyScrollScale: number) => {
    if (heroRef.current) {
      heroRef.current.style.transform = `scale(${copyScrollScale})` // Set the desired scale value
    }
  }

  useEffect(() => {
    const assets = assetsRef.current

    if (!assets) return
    let isClosed = false
    const callback = () => {
      if (isClosed) return
      setIsAssetsLoaded(true)
    }

    if (assets.isLoaded) {
      callback()
    } else {
      assets.addCallback(callback)
    }

    return () => {
      isClosed = true
    }
  }, [assetsRef])

  return (
    <section id="hero" className={isUniverseAnimationsEnabled ? 'hero-universe' : ''}>
      <div className="lp-Intro">
        {/* Header 72 + Global Banner 42 = 114px */}
        <div className="lp-Intro-gradient" />
        <Spacer size="114px" />
        <Spacer className="lp-IntroTop-spacer" size="0" size768="20px" size1012="50px" />
        {isDomReady && (
          <Suspense fallback={null}>
            {assetsRef &&
              (isUniverseAnimationsEnabled ? (
                <IntroHeroUniverse
                  setCopyScrollOpacity={setCopyScrollOpacity}
                  setCopyScrollScale={setCopyScrollScale}
                  isAssetsLoaded={isAssetsLoaded}
                />
              ) : (
                <IntroHero
                  setCopyScrollOpacity={setCopyScrollOpacity}
                  setCopyScrollScale={setCopyScrollScale}
                  assetsRef={assetsRef}
                />
              ))}
          </Suspense>
        )}

        {/* Hero */}
        <div ref={heroRef} className="lp-IntroHero">
          <Hero className="lp-IntroHero-hero" data-hpc align="center">
            <Hero.Heading size="1">{COPY.hero.heading}</Hero.Heading>
            <Hero.Description size="300">{COPY.hero.description}</Hero.Description>
          </Hero>
          <CtaForm location="hero" />
        </div>

        <Spacer className="lp-IntroVisuals-spacer" size="64px" size768="600px" size1012="660px" />

        {/* Carousel */}
        <HeroCarousel
          isVisible
          currentScreenIndex={currentCarouselScreenIndex}
          onAutoPlayTimeout={setCurrentCarouselScreenIndex}
          carouselAriaLabel={COPY.carousel.aria.label}
          playButtonAriaLabel={COPY.carousel.aria.playButton}
        />
      </div>

      {/* Carousel controls */}
      <HeroCarouselControls
        items={COPY.carousel.items as HeroCarouselControlsProps['items']}
        currentIndex={currentCarouselScreenIndex}
        onChange={setCurrentCarouselScreenIndex}
        carouselDescriptionId={COPY.carousel.aria.descriptionId}
      />

      {/* Logos */}
      <Logos />
    </section>
  )
}

try{ IntroHeroUniverse.displayName ||= 'IntroHeroUniverse' } catch {}
try{ IntroHero.displayName ||= 'IntroHero' } catch {}
try{ Intro.displayName ||= 'Intro' } catch {}